import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, DestroyRef, inject, Injector, input, signal, ViewEncapsulation } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { marker as _ } from '@biesbjerg/ngx-translate-extract-marker';
import { TranslatePipe } from '@ngx-translate/core';
import { simpleDeepClone } from '@phx-erp/shared';
import { CardModule } from 'primeng/card';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { combineLatest, filter, lastValueFrom, map, switchMap, tap } from 'rxjs';
import { BaseListCombinedService } from '../../../@core/services/base-list-combined.service';
import { Condition, IntegrationConnection, IntegrationConnectionResponse, IntegrationStatus, Operator, Product, ProductShopIntegrationLink, ProductShopIntegrationLinkInput } from '../../../generated/generated-types';
import { IntegrationConnectionService } from '../../services/integration-connection.service';
import { ProductShopIntegrationLinkService } from '../../services/product-shop-integration-link.service';
import { resolveIntegrationSyncFailureMessage } from '../../utils/integration-sync-failure-message';
import { ShopIntegrationCardComponent } from '../shop-integration-card/shop-integration-card.component';
import { extendedProductShopIntegrationLink, isActiveSelectedEvent } from '../shop-integration-card/types-interfaces';

interface ProductViewModelType {
  productId: string | undefined;  // Assumes productId may be undefined if model is not set
  isLoading: boolean;
  shopIntegrationLinks: extendedProductShopIntegrationLink[];
  countAllShopIntegrations: number;
  countAllProductShopIntegrationLinks: number;
}

@Component({
  selector: 'app-shop-integration-card-list',
  templateUrl: './shop-integration-card-list.component.html',
  encapsulation: ViewEncapsulation.Emulated,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    CardModule,
    ShopIntegrationCardComponent,
    TranslatePipe,
    ProgressSpinnerModule
  ]
})
export class ShopIntegrationCardListComponent {
  protected baseServices = inject(BaseListCombinedService);
  destroyRef = inject(DestroyRef);
  injector = inject(Injector);
  protected productShopIntegrationLinkService = inject(ProductShopIntegrationLinkService);
  protected integrationService = inject(IntegrationConnectionService);

  /** The Product Model */
  productModel = input.required<Product>();

  productId = computed(() => {
    return this.productModel()?.id;
  });

  /** If true, the shop integrations will be loaded from the server */
  isLoadingSig = signal<boolean>(true);

  /** The page size for the shop integrations */
  pageSize = signal<number>(10);

  /** The total number of shop integrations */
  countAllShopIntegrations = signal<number>(0);

  pageSizeProductShopIntegrationLinks = 1000;
  countAllProductShopIntegrationLinks = signal<number>(0);

  shopIntegrations = signal<IntegrationConnection[]>([]);

  shopIntegrationLinks = signal<extendedProductShopIntegrationLink[]>([]);

  // Please Only use in Template
  readonly viewModel = computed<ProductViewModelType>(() => ({
    productId: this.productId(),
    isLoading: this.isLoadingSig(),
    shopIntegrationLinks: this.shopIntegrationLinks(),
    countAllShopIntegrations: this.countAllShopIntegrations(),
    countAllProductShopIntegrationLinks: this.countAllProductShopIntegrationLinks()
  }));

  constructor() {
    toObservable(this.productId).pipe(
      filter((productId): productId is string => !!productId),
      switchMap((productId) => {
        const integrations$ = this.integrationService.getItems(
          { take: this.pageSize(), onlyForProducts: true },
          { enableStream: true }
        ).stream$.pipe(
          tap(() => this.setIsLoading(true)),
          map((integrations) => simpleDeepClone(integrations.getIntegrationConnectionsList)),
          tap(() => this.setIsLoading(false))
        );

        const productShopIntegrationLinks$ = this.productShopIntegrationLinkService.getItems(
          {
            take: this.pageSizeProductShopIntegrationLinks,
            searchFilter: {
              field: 'productId',
              value: productId,
              condition: Condition.AND,
              operator: Operator.equal,
            },
          },
          { enableStream: true }
        ).stream$.pipe(
          tap(() => this.setIsLoading(true)),
          map((integrationLinks) =>
            simpleDeepClone(integrationLinks.getProductShopIntegrationLinks.items) as extendedProductShopIntegrationLink[]
          ),
          tap(() => this.setIsLoading(false))
        );

        return combineLatest([integrations$, productShopIntegrationLinks$]).pipe(
          map(([integrations, shopLinks]) =>
            this.mergeIntegrationsWithLinks(integrations.items, shopLinks, productId)
          )
        );
      }),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(({ integrations, shopLinks }) => {
      this.shopIntegrations.set(integrations);
      this.shopIntegrationLinks.set(shopLinks);
    });
  }

  private mergeIntegrationsWithLinks(
    integrations: IntegrationConnection[],
    shopLinks: extendedProductShopIntegrationLink[],
    productId: string
  ): { integrations: IntegrationConnection[]; shopLinks: extendedProductShopIntegrationLink[] } {
    const mergedLinks = [...shopLinks];
    const shopLinkMap = new Map(mergedLinks.map((link) => [link.integrationId, link]));

    for (const integration of integrations) {
      const existingLink = shopLinkMap.get(integration.id);
      if (existingLink) {
        existingLink.isLoading = false;
        if (!existingLink.productId) {
          existingLink.productId = productId;
        }
      } else {
        mergedLinks.push({
          integrationId: integration.id,
          integration,
          inactive: true,
          id: null,
          productId,
        });
      }
    }

    return { integrations, shopLinks: mergedLinks };
  }

  /**
   * @description
   * Initializes the component after the view has been rendered and starts listening to real-time updates 
   * for integrations and product shop integration links.
   *
   * This method subscribes to two streams:
   * - **Integrations Stream (`integrations$`)**: Fetches the latest list of available integrations.
   * - **Product Shop Integration Links Stream (`productShopIntegrationLinks$`)**: Retrieves the links 
   *   between products and integrations, ensuring that each product has an associated integration.
   *
   * The two streams are **combined** using `combineLatest()`, ensuring:
   * - Any updates in **integrations** will be reflected in the product integration links.
   * - Any changes in **productShopIntegrationLinks** will properly map to the corresponding integration.
   * - The UI is kept up-to-date without manual refreshes.
   *
   * This implementation ensures that new integrations or changes to integration links are automatically
   * reflected in the UI without requiring additional API calls or page reloads.
   */
  async loadProductShopIntegrationLinks(page?: number) {
    const productId = this.productId();
    if (!productId) {
      return;
    }

    try {
      // Fetch product shop integration links from the service
      const result = await lastValueFrom(this.productShopIntegrationLinkService.getItems({
        take: page ? this.pageSizeProductShopIntegrationLinks : undefined,
        searchFilter: {
          field: 'productId',
          value: this.productId(),
          condition: Condition.AND,
          operator: Operator.equal
        },
      }
      ).mapSingle(x => x.getProductShopIntegrationLinks));

      // Clone and map the response items
      const shopLinks = simpleDeepClone(result.items) as extendedProductShopIntegrationLink[];
      const { shopLinks: mergedLinks } = this.mergeIntegrationsWithLinks(
        this.shopIntegrations(),
        shopLinks,
        productId
      );
      this.shopIntegrationLinks.set(mergedLinks);
      this.countAllProductShopIntegrationLinks.set(result.totalItems); // Update total count

    } catch (err: any) {
      console.error('Error loading product shop integration links:', err);
    }
  }

  /**
   * Loads the shop integrations from the server.
   * @param page The page number for pagination (optional).
   */
  async loadShopIntegrations(page?: number) {
    try {
      // Fetch shop integrations from the service
      const res = await lastValueFrom(
        this.integrationService.getItems({
          take: page ? this.pageSize() : undefined,
          onlyForProducts: true
        }).mapSingle((x) => x.getIntegrationConnectionsList)
      );

      // Clone the response to avoid mutating the original data
      const clonedRes = simpleDeepClone(res);
      this.shopIntegrations.set(clonedRes.items || []); // Update the shopIntegrations model
      this.countAllShopIntegrations.set(clonedRes.totalItems || 0); // Update total count
    } catch (err: any) {
      console.error('Error loading shop integrations:', err);
    }
  }

  /**
   * Saves or updates a shop integration link.
   * @param integration The shop integration link input.
   */
  async saveShopIntegrationLink(integration: ProductShopIntegrationLinkInput) {

    const matchingLinks = this.shopIntegrationLinks().filter(x =>
      (integration?.id && x.id === integration.id) ||
      (integration?.integrationId && x.integrationId === integration.integrationId)
    );
    const shopIntegrationLink = matchingLinks.find(link => link.id) ?? matchingLinks[0];

    // Prepare the input for saving
    const shopLinkInput: ProductShopIntegrationLinkInput = {
      id: integration.id ?? shopIntegrationLink?.id ?? undefined,
      inactive: integration.inactive,
      integrationId: integration.integrationId,
      productId: integration.productId ?? this.productId(),
    };

    if (integration.lastSyncStatus !== undefined) {
      shopLinkInput.lastSyncStatus = integration.lastSyncStatus;
    }
    if (integration.lastSyncMessage !== undefined) {
      shopLinkInput.lastSyncMessage = integration.lastSyncMessage;
    }
    if (integration.lastSyncDate !== undefined) {
      shopLinkInput.lastSyncDate = integration.lastSyncDate;
    }

    // Save the shop integration link and update the model
    const result = await this.productShopIntegrationLinkService.saveItem(shopLinkInput);
    this.shopIntegrationLinks.update((shopLinks) =>
      shopLinks.map((link) => (link.integrationId === shopLinkInput.integrationId ? result : link))
    );
  }

  /**
   * Handles the event when a shop integration's inactive state is toggled.
   * @param event The event containing the checkbox state and the associated shop integration link.
   */
  async onSelectedActiveShopIntegration(event: isActiveSelectedEvent) {
    const link = event.shopIntegrationLink;
    const shopLink = {
      id: link.id ?? undefined,
      inactive: !event.checkBox, // checked means active
      integrationId: link.integration?.id ?? link.integrationId,
      productId: link.productId ?? this.productId(),
    };
    await this.saveShopIntegrationLink(shopLink);

    const shopIntegrationLink = this.shopIntegrationLinks().find(
      (x) => x.integrationId === shopLink.integrationId
    );

    if (!shopIntegrationLink) {
      return;
    }

    if (event.checkBox) {
      await this.onSyncShopIntegration({ shopIntegrationLink });
    } else if (shopIntegrationLink.externalEntityId) {
      // Deactivated: push inactive state to the external shop when already synced
      await this.onSyncShopIntegration({ shopIntegrationLink }, { forceSync: true });
    }
  }

  /**
   * Syncs a shop integration link with the external service.
   * @param data The data containing the shop integration link.
   */
  async onSyncShopIntegration(data: { shopIntegrationLink: ProductShopIntegrationLink }, options: { forceSync: boolean } = { forceSync: false }) {
    try {
      if (!options.forceSync) {
        if (!this.isActiveShopIntegrationLink(data.shopIntegrationLink, { showNotifications: true })) {
          return;
        }
      }

      // Set the loading state for the specific link
      this.setShopIntegrationLinkLoadingState(data.shopIntegrationLink.integrationId, true);
      let response: IntegrationConnectionResponse;

      // Check if the external entity ID is missing and create or update the product accordingly
      // Here is basically, something to let us know what type of request method we need to use depending on the integration Type Plugin.
      // Maybe at some point we can create a flag in model to know if we need to use create or update method.
      if (!data.shopIntegrationLink.externalEntityId) {
        response = await this.productShopIntegrationLinkService.createProduct({
          productId: data.shopIntegrationLink.productId ?? this.productId(),
          integrationId: data.shopIntegrationLink.integrationId,
        });
      } else {
        response = await this.productShopIntegrationLinkService.updateProduct({
          productId: data.shopIntegrationLink.productId ?? this.productId(),
          integrationId: data.shopIntegrationLink.integrationId,
        });
      }

      await this.loadProductShopIntegrationLinks();
      await this.setShopIntegrationLinkLoadingState(data.shopIntegrationLink.integrationId, false);

      if (response.statusCode === '200' || response.statusCode === '204') {
        this.baseServices.notificationService.success(_('integration.synchronization-success'));
      } else {
        const reason = resolveIntegrationSyncFailureMessage(response, '');
        if (reason) {
          this.baseServices.notificationService.warning(
            _('integration.synchronization-failed-with-reason'),
            { reason },
            undefined,
            10000,
          );
        } else {
          this.baseServices.notificationService.warning(_('integration.synchronization-failed-please-check-api-connection'));
        }
      }

      return response;
    } catch (error: any) {
      console.error(error);
      const reason = error?.message
        ?? this.baseServices.i18nService.translate(_('integration.synchronization-failed-please-check-api-connection'));
      this.baseServices.notificationService.error(
        _('integration.synchronization-failed-with-reason'),
        { reason },
      );

      // Remove the loading state for the specific link
      this.setShopIntegrationLinkLoadingState(data.shopIntegrationLink.integrationId, false);
    }
  }

  /**
   * Sets the loading state for a specific shop integration link.
   * @param integrationId The ID of the shop integration link.
   * @param isLoading The loading state to set (true/false).
   */
  private setShopIntegrationLinkLoadingState(integrationId: string, isLoading: boolean) {
    this.shopIntegrationLinks.update((shopLinks) =>
      shopLinks.map((link) =>
        link.integrationId === integrationId ? { ...link, isLoading } : link
      )
    );
  }

  /**
   * Checks if a shop integration link is active.
   * @param shopIntegrationLink The shop integration link to check.
   * @param options Optional parameters including whether to show notifications.
   * @returns True if the link is active, false otherwise.
   */
  isActiveShopIntegrationLink(shopIntegrationLink: ProductShopIntegrationLink, options: { showNotifications: boolean } = { showNotifications: false }): boolean {
    if (shopIntegrationLink.inactive === true) {
      if (options.showNotifications) {
        this.baseServices.notificationService.warning(_('integration.link.integration-not-actived'));
      }
      return false;
    }
    return !shopIntegrationLink.inactive;
  }

  /**
   * Tests the API connection for a shop integration.
   * @param integration The shop integration to test.
   * @param id The ID of the shop integration.
   */
  async onTestApiConnection(integration: IntegrationConnection, id: string) {
    try {
      const response = await this.integrationService.testApiConnection(integration);
      const saved = await this.integrationService.saveItem({
        ...integration,
        lastSyncStatus: this.checkConnectionIntegrationStatus(response.statusCode),
        lastSyncMessage: response.errorMessage ?? response.errorDescription,
        lastSyncDate: new Date(),
      });
      await this.saveShopIntegrationLink({
        integrationId: saved.id,
        lastSyncStatus: saved.lastSyncStatus,
        lastSyncMessage: saved.lastSyncMessage,
        lastSyncDate: saved.lastSyncDate,
      });
    } catch (error: any) {
      console.error('Error:', error);
      const saved = await this.integrationService.saveItem({
        ...integration,
        lastSyncStatus: this.checkErrorConnectionIntegrationStatus(error),
        lastSyncMessage: error.message,
        lastSyncDate: new Date(),
      });
      await this.saveShopIntegrationLink({
        integrationId: saved.id,
        lastSyncStatus: saved.lastSyncStatus,
        lastSyncMessage: saved.lastSyncMessage,
        lastSyncDate: saved.lastSyncDate,
      });
    }
  }

  /**
   * Checks if a shop integration is already in the list.
   * @param integration The shop integration to check.
   * @param options Optional parameters including whether to show notifications.
   * @returns True if the integration is already in the list, false otherwise.
   */
  checkIfAlreadyInList(integration: IntegrationConnection, options: { showNotifications: boolean } = { showNotifications: false }): boolean {
    const isAlreadyInList = this.shopIntegrationLinks().some(
      (shopLink) => shopLink.integrationId === integration.id
    );

    if (isAlreadyInList && options.showNotifications) {
      this.baseServices.notificationService.warning(_('integration.alreadyInList'));
    }

    return isAlreadyInList;
  }

  /**
   * Maps HTTP status codes to IntegrationStatus enum values.
   * @param statusCode The HTTP status code to check.
   * @returns The corresponding IntegrationStatus value.
   */
  checkConnectionIntegrationStatus(statusCode: string): IntegrationStatus {
    switch (statusCode) {
      case '200':
        return IntegrationStatus.CONNECTED;
      case '429':
        return IntegrationStatus.TOO_MANY_REQUESTS;
      case '401':
        return IntegrationStatus.UNAUTHORIZED;
      case '403':
        return IntegrationStatus.FORBIDDEN;
      case '404':
        return IntegrationStatus.NOT_FOUND;
      case '500':
        return IntegrationStatus.INTERNAL_SERVER_ERROR;
      default:
        return IntegrationStatus.UNKNOWN;
    }
  }

  /**
   * Maps connection error to IntegrationStatus enum values.
   * @param error The error object to check.
   * @returns The corresponding IntegrationStatus value.
   */
  checkErrorConnectionIntegrationStatus(error: any): IntegrationStatus {
    return IntegrationStatus.ECONNREFUSED;
  }

  /**
   * TrackBy function for ngFor loops to optimize rendering by identifying items by their unique integration ID.
   * @param index The index of the current item in the loop.
   * @param shopIntegrationLink The current shop integration link item.
   * @returns The integration ID as the unique identifier.
   */
  trackByIntegrationId(index: number, shopIntegrationLink: ProductShopIntegrationLink): string {
    return shopIntegrationLink.integrationId;
  }

  setIsLoading(isLoading: boolean) {
    this.isLoadingSig.set(isLoading);
  }
}